import type { FC } from 'react';

import { apiEnums } from '@nodal/api';
import { t } from '@nodal/i18n';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Checkbox } from '@nodal/uikit/components/Checkbox';
import { PhoneNumberInput } from '@nodal/uikit/components/PhoneNumberInput';
import { PureSelect, Select } from '@nodal/uikit/components/Select';
import { TextInput } from '@nodal/uikit/components/TextInput';
import { useField, useFormikContext } from 'formik';
import { useCallback } from 'react';

import { states } from '@core/consts/states';
import { getOrdinalNumber } from '@core/utils';

import { pregnancyDetailInitialValues } from './initialValues';

import type {
  FacilityTypeOption,
  PregnancyDetail,
} from './MedicalRecordReview.interface';
import type { FormikValues, FormikHelpers } from 'formik';

const facilityTypeOptions: FacilityTypeOption[] = [
  {
    label: t('Hospital'),
    value: apiEnums.FacilityTypeEnum.Hospital,
  },
  {
    label: t('Birthing center'),
    value: apiEnums.FacilityTypeEnum.BirthCenter,
  },
  {
    label: t('Home birth'),
    value: apiEnums.FacilityTypeEnum.HomeBirth,
  },
  {
    label: t('Other'),
    value: apiEnums.FacilityTypeEnum.Other,
  },
];

const clinicCountryOptions = [
  {
    label: t('United States of America'),
    value: apiEnums.DoctorClinicCountryEnum.Us,
  },
  {
    label: t('Other'),
    value: apiEnums.DoctorClinicCountryEnum.Other,
  },
];

const currentYear = new Date().getFullYear();
const years: { label: string; value: string }[] = [];
for (let i = currentYear; i >= 1970; i--) {
  years.push({ label: i.toString(), value: `${i}-01-01` });
}

const PregnancyDetailFields: FC<{
  detail: PregnancyDetail;
  idx: number;
  editable: boolean;
  onClinicCountryChange: (value: string, name: string) => void;
  onFacilityTypeChange: (value: string, name: string) => void;
  setFieldValue: FormikHelpers<FormikValues>['setFieldValue'];
}> = ({
  detail,
  idx,
  editable,
  onClinicCountryChange,
  onFacilityTypeChange,
}) => {
  const deliveryDateFieldName = `pregnancy_details.${idx}.delivery_date`;
  const [deliveryDateField, deliveryDateMeta, deliveryDateHelpers] = useField(
    deliveryDateFieldName,
  );

  const clinicCountryFieldName = `pregnancy_details.${idx}.doctor_clinic_country`;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [clinicCountryField, clinicCountryMeta, clinicCountryHelpers] =
    useField(clinicCountryFieldName);

  const deliveryDateValue = deliveryDateField.value
    ? (() => {
        // Extract year directly from the date string to avoid timezone issues
        const dateStr = deliveryDateField.value;
        const year = dateStr.split('-')[0];
        return `${year}-01-01`;
      })()
    : deliveryDateField.value === null
    ? undefined
    : deliveryDateField.value;

  const clinicCountryValue =
    typeof clinicCountryField.value === 'string' &&
    clinicCountryField.value.toLowerCase().trim() === 'us'
      ? 'US'
      : clinicCountryField.value === null
      ? ''
      : clinicCountryField.value;

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-10" key={idx}>
      <div className="relative">
        <div className="flex absolute inset-0 items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="flex relative justify-start">
          <span className="pr-3 text-base font-semibold leading-7 text-gray-900 bg-white">
            {idx === 0
              ? 'Most Recent Pregnancy and Delivery'
              : t(
                  '{numberWithOrdinal} Pregnancy and Delivery',
                  `${getOrdinalNumber(idx + 1)}`,
                )}
          </span>
        </div>
      </div>

      <PureSelect
        label="Child's Year of Birth"
        name={deliveryDateFieldName}
        value={deliveryDateValue}
        onChange={(value) => deliveryDateHelpers.setValue(value)}
        error={
          deliveryDateMeta.touched && deliveryDateMeta.error
            ? deliveryDateMeta.error
            : undefined
        }
        options={years}
        placeholder="YYYY"
        className="lg:w-1/2"
        disabled={!editable}
      />

      {/* {idx !== 0 && (
        <Checkbox
          name={`pregnancy_details.${idx}.same_doctor_details_as_first`}
          label={t('Same doctor details as first pregnancy')}
          disabled={!editable}
        />
      )} */}

      {(idx === 0 || !detail.same_doctor_details_as_first) && (
        <>
          <TextInput
            label={"Doctor's Name (optional)"}
            placeholder="Doctor's Name"
            name={`pregnancy_details.${idx}.doctor_name`}
            type="text"
            disabled={!editable}
          />
          <PhoneNumberInput
            label={"Doctor's Phone Number (optional)"}
            name={`pregnancy_details.${idx}.doctor_phone_number`}
            placeholder={
              detail.doctor_clinic_country ===
              apiEnums.DoctorClinicCountryEnum.Other
                ? '+1 555 987 6543'
                : '(555) 987-6543'
            }
            type="text"
            className="lg:w-1/2"
            disabled={!editable}
            international={
              detail.doctor_clinic_country ===
              apiEnums.DoctorClinicCountryEnum.Other
            }
          />

          <TextInput
            label={'Clinic Name'}
            name={`pregnancy_details.${idx}.doctor_clinic_name`}
            placeholder="Clinic Name"
            type="text"
            disabled={!editable}
          />

          <div className="relative">
            <label
              htmlFor={clinicCountryFieldName}
              className="block text-sm font-medium leading-5 text-grey-forest-700"
            >
              Clinic Country
            </label>
            <select
              className={`w-full rounded-lg border transition-colors
                ${
                  clinicCountryMeta.touched && clinicCountryMeta.error
                    ? 'block mt-1 w-full rounded-md border-red-500 focus:border-red-500 focus:ring-red-500 shadow-sm sm:text-sm'
                    : 'block mt-1 w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 shadow-sm sm:text-sm'
                }
                `}
              id={clinicCountryFieldName}
              name={clinicCountryFieldName}
              value={clinicCountryValue}
              onChange={(e) => {
                const value = e.target.value;
                clinicCountryHelpers.setValue(value);
                onClinicCountryChange(value, `pregnancy_details.${idx}`);
              }}
              disabled={!editable}
            >
              <option value="" disabled>
                Clinic Country
              </option>
              {clinicCountryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {clinicCountryMeta.touched && clinicCountryMeta.error && (
              <div className="flex items-center mt-2 text-xs text-red-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  className="w-4 h-4 text-red-700"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span>
                  {clinicCountryMeta.error || 'This field is required'}
                </span>
              </div>
            )}
          </div>

          {detail.doctor_clinic_country ===
            apiEnums.DoctorClinicCountryEnum.Us && (
            <div className="grid grid-cols-1 gap-x-4 lg:grid-cols-2">
              <TextInput
                label={'Clinic City'}
                name={`pregnancy_details.${idx}.doctor_clinic_city`}
                placeholder="Clinic City"
                type="text"
                disabled={!editable}
              />
              <Select
                label={'Clinic State'}
                name={`pregnancy_details.${idx}.doctor_clinic_state`}
                options={states}
                placeholder="Clinic State"
                disabled={!editable}
              />
            </div>
          )}
          {detail.doctor_clinic_country ===
            apiEnums.DoctorClinicCountryEnum.Other && (
            <TextInput
              label={'Clinic Address'}
              name={`pregnancy_details.${idx}.doctor_clinic_international_address`}
              type="text"
              disabled={!editable}
              placeholder={t('Enter the address manually')}
            />
          )}
          <Select
            label={t('At what type of location did your delivery occur?')}
            name={`pregnancy_details.${idx}.facility_type`}
            options={facilityTypeOptions}
            placeholder={'Select a location type'}
            onChange={(value: string) =>
              onFacilityTypeChange(value, `pregnancy_details.${idx}`)
            }
            disabled={!editable}
            optionsPosition="top"
          />
          {detail.facility_type === apiEnums.FacilityTypeEnum.Hospital &&
            detail.doctor_clinic_country ===
              apiEnums.DoctorClinicCountryEnum.Us && (
              <div className="flex flex-col gap-8">
                <div>
                  <TextInput
                    label={'Hospital name'}
                    name={`pregnancy_details.${idx}.birth_center_name`}
                    type="text"
                    disabled={!editable}
                    placeholder={'Hospital name'}
                  />
                </div>
                <div className="grid grid-cols-1 gap-x-4 lg:grid-cols-2">
                  <TextInput
                    label={'Hospital city'}
                    name={`pregnancy_details.${idx}.birth_center_city`}
                    type="text"
                    disabled={!editable}
                    placeholder={'Hospital city'}
                  />
                  <Select
                    label={'Hospital state'}
                    name={`pregnancy_details.${idx}.birth_center_state`}
                    options={states}
                    placeholder={'Hospital state'}
                    disabled={!editable}
                  />
                </div>
              </div>
            )}
          {detail.facility_type === apiEnums.FacilityTypeEnum.Hospital &&
            detail.doctor_clinic_country ===
              apiEnums.DoctorClinicCountryEnum.Other && (
              <TextInput
                label={t('Hospital name and address')}
                name={`pregnancy_details.${idx}.delivery_international_address`}
                type="text"
                disabled={!editable}
                placeholder={t('Enter the name and address manually')}
              />
            )}
          {detail.facility_type === apiEnums.FacilityTypeEnum.BirthCenter &&
            detail.doctor_clinic_country ===
              apiEnums.DoctorClinicCountryEnum.Us && (
              <div className="flex flex-col gap-8">
                <div>
                  <TextInput
                    label={'Birthing center name'}
                    name={`pregnancy_details.${idx}.birth_center_name`}
                    type="text"
                    disabled={!editable}
                    placeholder={'Birthing center name'}
                  />
                </div>
                <div className="grid grid-cols-1 gap-x-4 lg:grid-cols-2">
                  <TextInput
                    label={'Birthing center city'}
                    name={`pregnancy_details.${idx}.birth_center_city`}
                    type="text"
                    disabled={!editable}
                    placeholder={'Birthing center city'}
                  />
                  <Select
                    label={'Birthing center state'}
                    name={`pregnancy_details.${idx}.birth_center_state`}
                    options={states}
                    placeholder={'Birthing center state'}
                    disabled={!editable}
                  />
                </div>
              </div>
            )}
          {detail.facility_type === apiEnums.FacilityTypeEnum.BirthCenter &&
            detail.doctor_clinic_country ===
              apiEnums.DoctorClinicCountryEnum.Other && (
              <TextInput
                label={t('Birthing center name and address')}
                name={`pregnancy_details.${idx}.delivery_international_address`}
                type="text"
                disabled={!editable}
                placeholder={t('Enter the name and address manually')}
              />
            )}
        </>
      )}
    </div>
  );
};

// NOTE: The component must be embedded inside Formik
export const PregnancyDetailsForm: FC<{
  pregnancyDetails: PregnancyDetail[];
  editable: boolean;
}> = ({ pregnancyDetails, editable }) => {
  const { setFieldValue, setFieldTouched } = useFormikContext<FormikValues>();

  const handleFacilityTypeChange = useCallback(
    (value: string, name: string) => {
      if (
        value === apiEnums.FacilityTypeEnum.Other ||
        value === apiEnums.FacilityTypeEnum.HomeBirth
      ) {
        setFieldValue(
          `${name}.delivery_address`,
          pregnancyDetailInitialValues.delivery_address,
        );
        setFieldValue(
          `${name}.delivery_international_address`,
          pregnancyDetailInitialValues.delivery_international_address,
        );
      }

      setFieldValue(`${name}.facility_type`, value);
      setFieldTouched(`${name}.facility_type`, false);
    },
    [setFieldTouched, setFieldValue],
  );

  const handleClinicCountryChange = useCallback(
    (value: string, name: string) => {
      if (value === apiEnums.DoctorClinicCountryEnum.Other) {
        setFieldValue(
          `${name}.doctor_clinic_address`,
          pregnancyDetailInitialValues.doctor_clinic_address,
        );
      }

      if (value === apiEnums.DoctorClinicCountryEnum.Us) {
        setFieldValue(
          `${name}.doctor_clinic_international_address`,
          pregnancyDetailInitialValues.doctor_clinic_international_address,
        );
      }

      setFieldValue(`${name}.doctor_clinic_country`, value);
      setFieldTouched(`${name}.doctor_clinic_country`, false);
    },
    [setFieldTouched, setFieldValue],
  );

  return (
    <>
      {pregnancyDetails?.map((detail: PregnancyDetail, idx: number) => (
        <PregnancyDetailFields
          key={idx}
          detail={detail}
          idx={idx}
          editable={editable}
          onClinicCountryChange={handleClinicCountryChange}
          onFacilityTypeChange={handleFacilityTypeChange}
          setFieldValue={setFieldValue}
        />
      ))}
    </>
  );
};
